import React, { Component } from 'react';
import LoadingIcons from 'react-loading-icons';

import RecipeGrid from '../../components/RecipeGrid';
import RemovableTagPill from '../../components/RemovableTagPill.js';
import RecipeService from '../../services/recipe';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowRight, faExclamationTriangle, faRefresh, faTemperatureEmpty, faPlus, faPaperclip, faMagicWandSparkles } from '@fortawesome/free-solid-svg-icons';

import '../../css/recipes.css';
import SortDropdown from '../../components/SortDropdown.js';
import { Player } from '@lottiefiles/react-lottie-player';
import loader from '../../loader/main_loading_animation.json';
import toast from 'react-hot-toast';
import config from "../../utils/config.js";

const RECIPES_PER_PAGE = 12;

const defaultRequestOptions = {
  offset: 0,
  hideDetails: true,
  limit: RECIPES_PER_PAGE,
}

class SearchIngredients extends Component {
  constructor(props) {
    super(props);
    document.title = `Ladle - Recipes`;

    // Extract ingredients from query params
    const queryParams = new URLSearchParams(this.props.location.search);
    const ingredients = queryParams.get('ingredients') ? queryParams.get('ingredients').split(',') : [];

    this.state = {
      recipes: undefined,
      user: this.props.user,
      totalRecipesLength: 0,
      ingredientsList: ingredients,
      options: {
        ...defaultRequestOptions,
        ingredients: ingredients,
      },
      isLoading: true,
      error: false,
      uploadedImage: null,
      imagePreview: null,
      isAnalyzingImage: false,
      showAnalysisAnimation: false,
    };
  }

  async componentDidMount() {
    this.updateOptions();
  }

  componentDidUpdate(prevProps) {
    const prevQueryParams = new URLSearchParams(prevProps.location.search);
    const currentQueryParams = new URLSearchParams(this.props.location.search);
    
    if (prevQueryParams.get('ingredients') !== currentQueryParams.get('ingredients')) {
      this.updateOptions();
    }
  }

  async updateOptions(existingOptions = {}) {
    let options = {
      ...defaultRequestOptions,
      ...existingOptions,
      ingredients: this.state.ingredientsList,
      offset: 0, // Start from the beginning when ingredients change
    };

    this.setState({ isLoading: true, options });
    this.getRecipes(options);
  }

  async getRecipes(options) {
    console.log({ msg: 'getRecipes', options });

    let { user } = this.props;
  
    try {
      this.setState({ isLoading: true });
    
      // Format the ingredients list into a comma-separated string
      const ingredientsString = options.ingredients.join(',');
    
      // Use the same API endpoint as publicSearchIngredients.js
      const response = await fetch(
        `${config.API_SERVER_URL}/api/v2/recipes/search-by-ingredients?` + 
        `ingredients=${encodeURIComponent(ingredientsString)}` +
        `&page=${options.offset / RECIPES_PER_PAGE + 1}` + 
        `&limit=${RECIPES_PER_PAGE}`
      );
    
      const data = await response.json();
    
      this.setState({
        recipes: data.recipes || [],
        options,
        isLoading: false,
        error: false,
        totalRecipesLength: data.recipes ? data.recipes.length : 0,
      });

    } catch (err) {
      console.error('getRecipes.error:', err);
      this.setState({ recipes: [], options, isLoading: false, error: err.message });
    }
  }
  handlePreviousClick = () => {
    let { options } = this.state;
    let offset = options.offset - RECIPES_PER_PAGE;
    if (offset < 0) offset = 0;
    options.offset = offset;
    this.setState({ options, isLoading: true }, () => this.getRecipes(options));
  };

  handleNextClick = () => {
    let { options } = this.state;
    let offset = options.offset + RECIPES_PER_PAGE;
    options.offset = offset;
    this.setState({ options, isLoading: true }, () => this.getRecipes(options));
  };

  reload = () => {
    this.setState({ recipes: undefined, error: false, isLoading: true });
    this.getRecipes(this.state.options);
  };

  // Image compression function
  compressImage = (file) => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions (max 800px width/height)
        const maxSize = 800;
        let { width, height } = img;
        
        if (width > height) {
          if (width > maxSize) {
            height = (height * maxSize) / width;
            width = maxSize;
          }
        } else {
          if (height > maxSize) {
            width = (width * maxSize) / height;
            height = maxSize;
          }
        }
        
        canvas.width = width;
        canvas.height = height;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, width, height);
        const compressedDataUrl = canvas.toDataURL('image/jpeg', 0.7);
        resolve(compressedDataUrl);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  // Handle file input change
  handleFileChange = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Image size should be less than 10MB');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      this.setState({
        uploadedImage: file,
        imagePreview: e.target.result
      });
    };
    reader.readAsDataURL(file);
  };

  // Remove uploaded image
  removeImage = () => {
    this.setState({
      uploadedImage: null,
      imagePreview: null
    });
  };

  // Extract ingredient name from full description
  extractIngredientName = (ingredientLine) => {
    // Remove common quantity patterns and get the main ingredient
    const cleaned = ingredientLine
      .replace(/^\d+(\.\d+)?\s*(cups?|tablespoons?|teaspoons?|tbsp|tsp|oz|ounces?|pounds?|lbs?|grams?|kg|ml|liters?)\s*/i, '')
      .replace(/^(a|an|the)\s+/i, '')
      .replace(/,.*$/, '') // Remove everything after comma
      .replace(/\(.*\)/, '') // Remove parentheses content
      .trim();
    
    return cleaned;
  };

  // Handle image analysis
  handleAnalyzeImage = async () => {
    if (!this.state.uploadedImage) return;
    
    this.setState({ isAnalyzingImage: true, showAnalysisAnimation: true });
    
    try {
      // Compress image before sending
      const compressedImage = await this.compressImage(this.state.uploadedImage);
      
      const response = await fetch(`${config.API_SERVER_URL}/api/v3/extract-ingredients`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          base64Image: compressedImage
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('API Response:', data);
      
      // Check for error response
      if (data.error) {
        toast.error(data.error);
        this.setState({ isAnalyzingImage: false, showAnalysisAnimation: false });
        return;
      }
      
      // Extract ingredients from API response
      if (data.ingredients && Array.isArray(data.ingredients)) {
        const newIngredients = [...this.state.ingredientsList];
        
        // Handle the current backend response format where ingredients include JSON formatting
        let cleanedIngredients = [];
        
        if (data.success && data.ingredients) {
          // The backend is currently returning malformed JSON strings, so we need to clean them
          cleanedIngredients = data.ingredients
            .map(item => {
              // Remove JSON formatting characters and quotes
              return item
                .replace(/^"ingredients":\s*\[/, '') // Remove opening
                .replace(/\]$/, '') // Remove closing bracket
                .replace(/^"|"$|,$/, '') // Remove quotes and trailing commas
                .trim();
            })
            .filter(item => item && !item.includes('"ingredients"') && !item.includes('[') && !item.includes(']'));
        } else {
          cleanedIngredients = data.ingredients;
        }
        
        cleanedIngredients.forEach(ingredientLine => {
          // Extract just the ingredient name from full descriptions like "1 cup all-purpose flour"
          const cleanIngredient = this.extractIngredientName(ingredientLine);
          
          if (cleanIngredient && !newIngredients.includes(cleanIngredient)) {
            newIngredients.push(cleanIngredient);
          }
        });
        
        // Show success animation for 2 seconds then update ingredients and search
        setTimeout(() => {
          this.setState({ 
            isAnalyzingImage: false, 
            showAnalysisAnimation: false,
            ingredientsList: newIngredients,
            uploadedImage: null,
            imagePreview: null
          });
          
          // Update the URL with new ingredients
          const queryParams = new URLSearchParams();
          if (newIngredients.length > 0) {
            queryParams.set('ingredients', newIngredients.join(','));
          }
          
          // Update browser URL
          const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
          window.history.pushState({}, '', newUrl);
          
          // Update options and search for recipes
          this.updateOptions();
          
          toast.success(`Found ${cleanedIngredients.length} ingredients! Searching for recipes...`);
        }, 2000);
        
      } else {
        toast.error('No ingredients found in the image');
        this.setState({ isAnalyzingImage: false, showAnalysisAnimation: false });
      }
      
    } catch (error) {
      console.error('Error analyzing image:', error);
      toast.error('Failed to analyze image. Please try again.');
      this.setState({ isAnalyzingImage: false, showAnalysisAnimation: false });
    }
  };

  // Add ingredient to list
  addIngredient = (ingredient) => {
    if (ingredient.trim() && !this.state.ingredientsList.includes(ingredient.trim())) {
      const newIngredients = [...this.state.ingredientsList, ingredient.trim()];
      this.setState({ ingredientsList: newIngredients });
      
      // Update URL
      const queryParams = new URLSearchParams();
      queryParams.set('ingredients', newIngredients.join(','));
      const newUrl = `${window.location.pathname}?${queryParams.toString()}`;
      window.history.pushState({}, '', newUrl);
      
      // Update search
      this.updateOptions();
    }
  };

  // Remove ingredient from list
  removeIngredient = (ingredientToRemove) => {
    const newIngredients = this.state.ingredientsList.filter(ingredient => ingredient !== ingredientToRemove);
    this.setState({ ingredientsList: newIngredients });
    
    // Update URL
    const queryParams = new URLSearchParams();
    if (newIngredients.length > 0) {
      queryParams.set('ingredients', newIngredients.join(','));
    }
    const newUrl = newIngredients.length > 0 ? `${window.location.pathname}?${queryParams.toString()}` : window.location.pathname;
    window.history.pushState({}, '', newUrl);
    
    // Update search
    this.updateOptions();
  };

  render() {
    const { recipes, totalRecipesLength, options, isLoading, error, ingredientsList, uploadedImage, imagePreview, isAnalyzingImage, showAnalysisAnimation } = this.state;

    const loadingText = `Loading Recipes`;

    let contentSectionHtml;

    // Generate tag pills for ingredients
    const tagPillsHtml = ingredientsList.map((ingredient, index) => (
      <RemovableTagPill 
        key={index} 
        label={ingredient} 
        color='green' 
        size='large' 
        onRemove={() => this.removeIngredient(ingredient)}
      />
    ));

    // Input field with image upload
    const ingredientInputHtml = (
      <div className="mb-6">
        <div className="relative flex items-center">
          <input
            type="text"
            placeholder="Add ingredients (e.g., chicken, rice, tomatoes)"
            className="flex-1 px-4 py-3 border border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent"
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                this.addIngredient(e.target.value);
                e.target.value = '';
              }
            }}
          />
          
          {/* Image Upload */}
          <div className="relative">
            <input
              type="file"
              accept="image/*"
              onChange={this.handleFileChange}
              className="hidden"
              id="image-upload"
            />
            <label
              htmlFor="image-upload"
              className="flex items-center justify-center px-3 py-3 bg-gray-100 border-t border-b border-gray-300 hover:bg-gray-200 cursor-pointer transition-colors"
              title="Upload image to scan for ingredients"
            >
              <FontAwesomeIcon icon={faPaperclip} size="sm" className="text-gray-600" />
            </label>
          </div>
          
          <button
            onClick={(e) => {
              const input = e.target.parentElement.querySelector('input[type="text"]');
              if (input.value.trim()) {
                this.addIngredient(input.value);
                input.value = '';
              }
            }}
            className="px-4 py-3 bg-green-600 text-white rounded-r-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors"
          >
            <FontAwesomeIcon icon={faPlus} size="sm" />
          </button>
        </div>
        
        {/* Image Preview */}
        {imagePreview && (
          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-start space-x-4">
              <img 
                src={imagePreview} 
                alt="Uploaded" 
                className="w-20 h-20 object-cover rounded-lg"
              />
              <div className="flex-1">
                <p className="text-sm text-gray-600 mb-2">Image uploaded successfully!</p>
                <div className="flex space-x-2">
                  <button
                    onClick={this.handleAnalyzeImage}
                    disabled={isAnalyzingImage}
                    className="px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {isAnalyzingImage ? 'Analyzing...' : '🔍 Scan for ingredients'}
                  </button>
                  <button
                    onClick={this.removeImage}
                    className="px-4 py-2 bg-gray-500 text-white text-sm rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    Remove
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    );

    let recipesToShow = recipes ? recipes : [];
    console.log(recipes, "searchrecipes");
    if (error) {
      contentSectionHtml = (
        <div className="error-container">
          <p className="error-message">
            <FontAwesomeIcon icon={faExclamationTriangle} style={{ paddingRight: '12px' }} />
            Unable to load recipes. Please check your server connection and try again.
          </p>
          <button className='outline' onClick={this.reload}>
            <FontAwesomeIcon icon={faRefresh} style={{ paddingRight: '12px' }} />
            Retry
          </button>
        </div>
      );
    } else if (isLoading) {
      contentSectionHtml = (
        <div className="loading-container">
          <p style={{ fontSize: '16pt', marginBottom: '32px' }}>{loadingText}</p>
          <LoadingIcons.TailSpin height="48px" stroke="#0e4621" fill="#0e4621" />
        </div>
      );
    } else if (recipes.length === 0) {
      contentSectionHtml = (
        <div className="no-results-container">
          <p className="no-results-message">
            <FontAwesomeIcon icon={faTemperatureEmpty} style={{ paddingRight: '12px' }} />
            No recipes found
          </p>
        </div>
      );
    } else {
      contentSectionHtml = (
        <div>
          <div className="recipe-grid-container">
            <RecipeGrid recipes={recipesToShow} type='consumer' user={this.props.user} tenant={this.props.tenant} handleTagClick={this.handleTagClick} />
          </div>
          <div className="flex justify-center space-x-4 my-8">
  <button
    className={`px-6 py-3 text-lg font-bold text-white bg-green-600 rounded-full shadow-lg hover:bg-green-700 transition duration-300 ease-in-out ${
      options.offset === 0 ? 'opacity-50 cursor-not-allowed' : ''
    }`}
    disabled={options.offset === 0}
    onClick={this.handlePreviousClick}
  >
    <FontAwesomeIcon icon={faArrowLeft} className="mr-2" /> Previous
  </button>

  <button
    className={`px-6 py-3 text-lg font-bold text-white bg-green-600 rounded-full shadow-lg hover:bg-green-700 transition duration-300 ease-in-out ${
      recipes.length < RECIPES_PER_PAGE ? 'opacity-50 cursor-not-allowed' : ''
    }`}
    disabled={recipes.length < RECIPES_PER_PAGE}
    onClick={this.handleNextClick}
  >
    Next <FontAwesomeIcon icon={faArrowRight} className="ml-2" />
  </button>
</div>

        </div>
      );
    }

    return (
      <div className="search-ingredients-container p-8">
        <div className="content-menu-bar">
          {tagPillsHtml}
        </div>
        
        <h1 className='text-2xl mb-3 font-bold text-[#0e4621] mt-2 mb-3'>Recipes based on your Ingredients </h1>
      
        {ingredientInputHtml}
        
        {contentSectionHtml}
        
        {/* Analysis Animation Overlay */}
        {showAnalysisAnimation && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-sm mx-4 text-center">
              <Player
                autoplay
                loop
                src={loader}
                style={{ height: '150px', width: '150px' }}
              />
              <div className="mt-4">
                <div className="flex items-center justify-center space-x-2 mb-2">
                  <FontAwesomeIcon icon={faMagicWandSparkles} className="text-[#0e4621] text-xl" />
                  <h3 className="text-lg font-semibold text-gray-800">Analyzing Image</h3>
                </div>
                <p className="text-gray-600 text-sm">
                  Scanning your image for ingredients...
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default SearchIngredients;
